import SocioSvg from '~/icons/Socio'
import { Button, Card } from '~/design-system/primitives'
import { cn } from '~/lib/cn'
import { Pig, Cart, Bag, TenDay, OneDay } from '~/icons'
import { useLoaderData } from '@remix-run/react'
import type { loader } from '~/routes/myaccount'
import { BannterGoToExtract } from '../CashbackCard/GoToExtractBannter'
import { BannerOptIn } from '../CashbackCard/OptInBannter'
import { FAQ_URL } from '~/design-system/helpers/utilsValidation'

const CASHBACK_CONSTANTS = {
  RATE: 5,
  THRESHOLD: 100,
  MAX_MONTHLY: 60,
  ACCUMULATION_DEADLINE: 10,
  USAGE_START_DATE: 11,
} as const


interface SocioSwiftProps {
  user?: {
    profile?: {
      isCorporate?: boolean
    }
  }
  cashbackBalance?: {
    error?: boolean
    amount?: number
  }
}

export default function SocioSwift() {
  const { user, cashbackBallance } = useLoaderData<typeof loader>()
  const isCorporate = user?.profile?.isCorporate

  const swiftVcConsent = user?.profile?.consents?.find(consent => consent.key === "swift_vc");

  const handleFAQClick = () => {
    window.open(FAQ_URL, '_blank', 'noopener,noreferrer')
  }

  const accumulationBorderClasses = cn(
    'py-8 px-[0.813rem] relative z-10',
    'lg:border-l-[0.25rem] lg:border-dashed lg:border-l-grays-60',
    'lg:border-r-[0.25rem] lg:border-r-grays-60',
    'lg:border-t-[0.25rem] lg:border-t-primary-orange',
    'lg:border-b-0',

    'border-l-[0.25rem] border-dashed  border-l-primary-orange',
    'border-t-[0.25rem] border-dashed  border-t-grays-60',
    'border-b-[0.25rem] border-b-grays-60'
  )

  const usageBorderClasses = cn(
    'pt-10 pb-8 lg:py-8 px-2 lg:px-3 relative',
    'lg:border-t-[3px] lg:border-grays-60',
    'lg:border-l-0',
    'border-l-[0.25rem] border-l-grays-60'
  )

  return (
    <Card
      className={cn('lg:w-6/6 self-stretch gap-0 px-4 py-6 lg:px-6 lg:py-10')}
      color="primary"
      border
    >
      <header className="flex flex-col items-start gap-5 gap-y-4 self-stretch">
        <SocioSvg
          width="90"
          height="auto"
          role="img"
          aria-label="Logo Sócio Swift"
        />
      </header>

      <section className="flex lg:flex-row flex-col gap-4 items-start mt-10">
        <div className="bg-gray-10 py-4 pb-10 px-[0.938rem] lg:px-10 rounded-lg flex-1">
          <h1
            className="text-xl lg:text-lg uppercase font-bold mb-4 mx-auto table font-oswald text-center -tracking-[-0.125rem]"
            id="cashback-instructions"
          >
            <span className="text-primary-orange">Como acumular </span>
            o cashback em suas compras?
          </h1>

          <div
            className="grid grid-cols-1 lg:[grid-template-columns:1.80fr_2fr] mt-10"
            role="group"
            aria-labelledby="cashback-instructions"
          >
            <div className={accumulationBorderClasses}>
              <div
                className="absolute bottom-[-1.375rem] left-[-1.375rem] lg:left-auto lg:top-[-1.375rem] lg:right-[-1.375rem]"
                aria-hidden="true"
              >
                <Pig width="40" height="40" />
              </div>

              <div
                className="absolute top-[-1.375rem] left-[-1.375rem] lg:top-[-1.375rem] lg:left-[-1.375rem]"
                aria-hidden="true"
              >
                <Bag width="40" height="40" />
              </div>

              <div
                className="absolute top-[-1.375rem] right-[-1.375rem] lg:top-auto lg:bottom-[-1.375rem] lg:left-[-1.375rem]"
                aria-hidden="true"
              >
                <OneDay width="42" height="42" />
              </div>

              <div
                className="absolute bottom-[-1.375rem] right-[-1.375rem]"
                aria-hidden="true"
              >
                <TenDay width="42" height="42" />
              </div>

              <h2 className="font-oswald font-bold text-base -tracking-[-0.125rem] uppercase text-center mx-auto table">
                <span className="text-primary-orange">Acumule </span>
                seu cashback até o dia {CASHBACK_CONSTANTS.ACCUMULATION_DEADLINE} de cada mês.
              </h2>

              <p className="mt-4 text-center mx-auto table text-sm p-1">
                A cada R$ {CASHBACK_CONSTANTS.THRESHOLD},00 em compras realizadas{' '}
                <strong className="font-semibold">
                  até o dia {CASHBACK_CONSTANTS.ACCUMULATION_DEADLINE} de cada mês
                </strong>, você ganha{' '}
                <strong className="font-semibold">
                  R$ {CASHBACK_CONSTANTS.RATE},00 de cashback {' '}
                </strong>{' '}
                para utilizar.
              </p>
            </div>

            <div className={usageBorderClasses}>
              <div
                className="absolute bottom-[-1.375rem] z-10 left-[-1.375rem] lg:left-auto lg:top-[-1.375rem] lg:right-[-1.375rem]"
                aria-hidden="true"
              >
                <Cart width="40" height="40" />
              </div>

              <h2 className="font-oswald font-bold text-base -tracking-[-0.125rem] uppercase text-center mx-auto table">
                <span className="text-primary-orange">Utilize seu saldo </span>
                nas compras a partir do dia {CASHBACK_CONSTANTS.USAGE_START_DATE} do mês vigente.
              </h2>

              <p className="mt-4 px-0 lg:px-1 text-center mx-auto table text-sm">
                <strong className="font-semibold">
                  Você pode acumular até R$ {CASHBACK_CONSTANTS.MAX_MONTHLY},00 no mês {' '}
                </strong>{' '}
                e utilizar o valor em qualquer compra feita no site, aplicativo ou loja física da Swift.{' '}
                <strong>Aproveite!</strong>
              </p>

              <p className="mt-4 px-1 text-center mx-auto table text-sm">
                Seu cashback só poderá ser utilizado entre o dia {CASHBACK_CONSTANTS.USAGE_START_DATE} e o último dia do mês.
              </p>
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-4 w-full lg:w-[18rem] mt-4 bg-gray-10 rounded-lg">
          {swiftVcConsent?.value && !cashbackBallance?.error && !isCorporate && (
            <BannterGoToExtract />
          )}

          {!swiftVcConsent?.value && !isCorporate && (
            <BannerOptIn />
          )}

          <section className="p-4" aria-labelledby="faq-heading">
            <h2
              id="faq-heading"
              className="font-oswald text-primary-orange text-xl text-center mx-auto table uppercase font-bold -tracking-[-0.125rem]"
            >
              FAQ
            </h2>

            <p className="text-sm text-center mx-auto mt-6">
              Reunimos as perguntas mais comuns para facilitar sua experiência.
              Confira e encontre rapidamente o que precisa.
            </p>

            <Button
              onPress={handleFAQClick}
              size="sm"
              className="w-full mt-4"
              color="primary"
              type="button"
              aria-describedby="faq-description"
            >
              Acessar FAQ
            </Button>

            <span
              id="faq-description"
              className="sr-only"
            >
              Abre em nova aba
            </span>
          </section>
        </aside>
      </section>
    </Card>
  )
}
